import React from 'react';
import { Link } from 'react-router-dom';
import { Show, DeleteButton, EditButton } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Tag, Space, theme } from 'antd';
import type { HealthSyncRecordAdminDto } from '@wellverse/shared-ts';
import {
  HealthSyncCategory,
  HealthSyncPlatform,
} from '@wellverse/shared-ts';
import { Field } from '../../components/Field';

const platformColors: Record<HealthSyncPlatform, string> = {
  [HealthSyncPlatform.APPLE_HEALTH]: '#52c41a',
  [HealthSyncPlatform.HEALTH_CONNECT]: '#1890ff',
  [HealthSyncPlatform.PARTNER]: '#722ed1',
};

const categoryColors: Record<HealthSyncCategory, string> = {
  [HealthSyncCategory.CHARACTERISTIC]: '#898989',
  [HealthSyncCategory.BODY]: '#1890ff',
  [HealthSyncCategory.VITAL]: '#ff6b6b',
  [HealthSyncCategory.ACTIVITY]: '#3ecf8e',
  [HealthSyncCategory.NUTRITION]: '#f5a623',
  [HealthSyncCategory.SLEEP]: '#722ed1',
  [HealthSyncCategory.WORKOUT]: '#13c2c2',
  [HealthSyncCategory.OTHER]: '#595959',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

export const HealthSyncRecordShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow<HealthSyncRecordAdminDto>({
    resource: 'health-sync-records',
  });
  const item = queryResult?.data?.data;

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  return (
    <Show
      resource="health-sync-records"
      headerButtons={({ defaultButtons }) => (
        <>
          {defaultButtons}
          <EditButton resource="health-sync-records" recordItemId={item.id} />
          <DeleteButton resource="health-sync-records" recordItemId={item.id} />
        </>
      )}
    >
      <div style={{ padding: '8px 0' }}>
        <Field label="ID">{item.id}</Field>
        <Field label="User">
          <Space direction="vertical" size={0}>
            <Link to={`/users/show/${item.userId}`}>
              {item.userFullName || item.userId}
            </Link>
            {item.userEmail && (
              <span style={{ fontSize: 13, color: token.colorTextSecondary }}>
                {item.userEmail}
              </span>
            )}
          </Space>
        </Field>
        <Field label="Platform">
          <Tag style={tagStyle(platformColors[item.platform] || '#898989')}>
            {item.platform}
          </Tag>
        </Field>
        <Field label="Category">
          <Tag style={tagStyle(categoryColors[item.category] || '#898989')}>
            {item.category}
          </Tag>
        </Field>
        <Field label="Data Type">{item.dataType}</Field>
        <Field label="Value">
          {item.numericValue != null
            ? `${item.numericValue}${item.unit ? ` ${item.unit}` : ''}`
            : '—'}
        </Field>
        <Field label="Source Name">{item.sourceName || '—'}</Field>
        <Field label="External Key">{item.externalKey}</Field>
        <Field label="Start Time">
          {new Date(item.startTime).toLocaleString()}
        </Field>
        <Field label="End Time">
          {item.endTime ? new Date(item.endTime).toLocaleString() : '—'}
        </Field>
        {item.payload && (
          <Field label="Payload">
            <pre
              style={{
                background: token.colorFillAlter,
                padding: 8,
                borderRadius: 4,
                overflow: 'auto',
                maxHeight: 400,
              }}
            >
              {JSON.stringify(item.payload, null, 2)}
            </pre>
          </Field>
        )}
        <Field label="Created At">
          {new Date(item.createdAt).toLocaleString()}
        </Field>
        <Field label="Updated At">
          {new Date(item.updatedAt).toLocaleString()}
        </Field>
      </div>
    </Show>
  );
};
